/*
 * Clipboard - Zwischenablage-Operationen
 *
 * Verwaltet Copy/Cut/Paste mit Rich-Text-Unterstützung:
 * - Copy/Cut: Text + HTML (für Rich-Text-Paste in externe Programme)
 * - Paste: HTML-Import (aus Word/Notes etc.) oder Plain-Text
 */

import { ctx } from './appContext.js'
import { PaneId } from './config.js'
import { richTextToHtml, markdownToRichText } from './RichTextOps.js'
import { saveContent } from './persistence.js'

/**
 * Baut HTML aus den RT-Clipboard-Daten (für Rich-Text-Paste in andere Programme).
 * Unterstützt Inline-Formate (bold, italic etc.) und Block-Formate
 * (Überschriften, Listen, Blockquotes, Codeblöcke).
 *
 * Wichtig: Die Eingaben sind ABSATZ-basiert (ein Eintrag pro selektiertem Absatz),
 * NICHT zeilen-basiert. Weiche Zeilenumbrüche (\n innerhalb eines Absatzes) bleiben
 * im jeweiligen paragraphTexts-Eintrag erhalten und werden hier zu <br>. Würden wir
 * stattdessen auf \n splitten, verrutschten die absatz-relativen Format-Spans bei
 * jedem weichen Umbruch (Bug: Fett landete eine Zeile zu früh).
 *
 * @param {string[]|null} paragraphTexts - Text pro selektiertem Absatz (Soft-Breaks erhalten)
 * @param {Array<{formats: Array, links: Array}>|null} rtData - RT-Daten pro Absatz (1:1 zu paragraphTexts)
 * @param {Object|null} outlineInfo - Block-Attribute pro Absatz-Index ({headingLevel, listType, listLevel, blockquote, codeBlock, ...})
 * @returns {string|null} HTML oder null wenn keine Formate/Blöcke vorhanden
 */
export function _buildClipboardHtml(paragraphTexts, rtData, outlineInfo) {
    if (!paragraphTexts || !rtData || rtData.length === 0) return null
    const hasFormats = rtData.some(rd => rd.formats && rd.formats.length > 0)
    const hasBlocks = outlineInfo && Object.keys(outlineInfo).length > 0
    if (!hasFormats && !hasBlocks) return null

    // Inline-Formate pro Absatz rendern; weiche Umbrüche → <br>
    const paraHtml = paragraphTexts.map((paraText, i) => {
        const rd = rtData[i]
        const inline = (rd && rd.formats && rd.formats.length > 0)
            ? richTextToHtml(paraText, rd.formats)
            : _escapeHtmlSimple(paraText)
        return inline.replace(/\n/g, '<br>')
    })

    // Ohne Block-Info: einfach mit <br> joinen (Rückwärtskompatibilität)
    if (!hasBlocks) return paraHtml.join('<br>')

    // Block-Strukturen aufbauen (Listen + Codeblöcke gruppieren)
    const result = []
    let openLists = [] // Stack: [{tag: 'ul'|'ol', level: n}]
    let inCode = false

    const closeLists = () => {
        while (openLists.length > 0) {
            const closed = openLists.pop()
            result.push(`</${closed.tag}>`)
        }
    }
    const closeCode = () => {
        if (inCode) { result.push('</code></pre>'); inCode = false }
    }

    for (let i = 0; i < paraHtml.length; i++) {
        const info = outlineInfo[i]
        const lineHtml = paraHtml[i]

        // Codeblock: aufeinanderfolgende Code-Absätze in EIN <pre><code> bündeln
        if (info && info.codeBlock) {
            closeLists()
            if (!inCode) { result.push('<pre><code>'); inCode = true }
            else { result.push('\n') }
            result.push(lineHtml)
            continue
        }
        closeCode()

        if (info && info.listType) {
            const tag = info.listType === 'numbered' ? 'ol' : 'ul'
            const level = info.listLevel || 0

            // Listen-Stack anpassen: schließen bis zum richtigen Level
            while (openLists.length > level + 1) {
                const closed = openLists.pop()
                result.push(`</${closed.tag}>`)
            }
            // Öffnen bis zum richtigen Level
            while (openLists.length <= level) {
                result.push(`<${tag}>`)
                openLists.push({ tag, level: openLists.length })
            }
            // Tag-Wechsel auf gleichem Level (ul→ol oder umgekehrt)
            if (openLists.length > 0 && openLists[openLists.length - 1].tag !== tag) {
                const closed = openLists.pop()
                result.push(`</${closed.tag}>`)
                result.push(`<${tag}>`)
                openLists.push({ tag, level: closed.level })
            }

            result.push(`<li>${lineHtml}</li>`)
        } else {
            // Alle offenen Listen schließen
            closeLists()

            // Block-Element bestimmen: Überschrift > Blockquote > Absatz
            const hLevel = info && info.headingLevel
            if (hLevel >= 1 && hLevel <= 6) {
                result.push(`<h${hLevel}>${lineHtml}</h${hLevel}>`)
            } else if (info && info.blockquote) {
                result.push(`<blockquote>${lineHtml}</blockquote>`)
            } else {
                result.push(`<p>${lineHtml}</p>`)
            }
        }
    }

    // Restliche offene Strukturen schließen
    closeCode()
    closeLists()

    return result.join('')
}

/**
 * Konvertiert markierten Console-Output (Markdown) zu HTML für Rich-Text-Paste
 * in externe Programme. Ohne diese Umwandlung landen `**fett**`, `# Überschrift`
 * etc. wörtlich in Word/Notes. Der Plain-Text (Markdown) wird zusätzlich
 * geschrieben — interne Pastes (Editor/Console) rekonstruieren daraus die Formate.
 *
 * @param {string} mdText - Markdown-Text (zeilenweise, Codeblöcke via ``` umrahmt)
 * @returns {string|null} HTML oder null
 */
export function _consoleMarkdownToHtml(mdText) {
    if (!mdText) return null

    const lines = mdText.split('\n')
    const paragraphTexts = []
    const rtData = []
    const outlineInfo = {}
    let inCodeFence = false

    for (const line of lines) {
        // ```-Zaun togglet den Codeblock-Modus; die Zaun-Zeile selbst entfällt
        if (line.trim() === '```') { inCodeFence = !inCodeFence; continue }

        const idx = paragraphTexts.length
        if (inCodeFence) {
            paragraphTexts.push(line)
            rtData.push({ formats: [], links: [] })
            outlineInfo[idx] = { headingLevel: 0, listType: null, listLevel: 0, blockquote: false, codeBlock: true }
        } else {
            const rt = markdownToRichText(line)
            paragraphTexts.push(rt.text)
            rtData.push({ formats: rt.formats, links: rt.links })
            outlineInfo[idx] = {
                headingLevel: rt.headingLevel, listType: rt.listType,
                listLevel: rt.listLevel, blockquote: rt.blockquote, codeBlock: false
            }
        }
    }

    return _buildClipboardHtml(paragraphTexts, rtData, outlineInfo)
}

/** Einfaches HTML-Escape (für Zeilen ohne RT-Formate) */
function _escapeHtmlSimple(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * Schreibt Text und optional HTML in die Zwischenablage.
 * @param {string} text - Plain-Text
 * @param {string|null} html - HTML oder null
 */
export async function _writeClipboard(text, html) {
    try {
        if (html && navigator.clipboard.write) {
            const items = [new ClipboardItem({
                'text/plain': new Blob([text], { type: 'text/plain' }),
                'text/html': new Blob([html], { type: 'text/html' })
            })]
            await navigator.clipboard.write(items)
        } else {
            await navigator.clipboard.writeText(text)
        }
    } catch (err) {
        // Fallback: nur Plain-Text
        try { await navigator.clipboard.writeText(text) } catch { /* ignorieren */ }
        console.warn('Clipboard write failed:', err)
    }
}

/**
 * Schreibt eine Console-Selektion (Markdown) in die Zwischenablage: Plain-Text
 * bleibt Markdown (für internes Re-Paste), zusätzlich formatiertes HTML für
 * externe Programme. Ohne das HTML erscheinen Markdown-Marker (`**fett**`,
 * `# Überschrift`) wörtlich z.B. in Word/Notes.
 * @param {string} text - Markdown-Text der Selektion
 */
export async function writeConsoleSelection(text) {
    if (!text) return
    const html = _consoleMarkdownToHtml(text)
    await _writeClipboard(text, html)
}

/**
 * Liefert den aktiven Editor fuer Clipboard-Operationen. Bei offenem
 * Personal-Dict- oder Memory-Editor: dessen ProseEditor; sonst der Haupt-Editor.
 * @returns {{editor: object, isMemory: boolean}|null}
 */
function _getActiveEditor() {
    if (ctx.personalDictEditor && ctx.personalDictEditor.isActive()
        && ctx.personalDictEditor.proseEditor
    ) {
        return { editor: ctx.personalDictEditor.proseEditor, isMemory: true }
    }
    if (ctx.memoryEditor && ctx.memoryEditor.isActive()
        && ctx.memoryEditor.proseEditor
    ) {
        return { editor: ctx.memoryEditor.proseEditor, isMemory: true }
    }
    if (ctx.editor) return { editor: ctx.editor, isMemory: false }
    return null
}

/**
 * Kopiert selektierten Text in die Zwischenablage.
 */
export async function handleCopy() {
    const target = _getActiveEditor()
    if (!target) return

    const text = target.editor.handleCopy()
    if (text) {
        const clip = target.editor.input._internalClipboard
        const rtData = clip?.rtData || null
        const outlineInfo = clip?.outlineInfo || null
        const paragraphTexts = clip?.paragraphTexts || null
        const html = _buildClipboardHtml(paragraphTexts, rtData, outlineInfo)
        await _writeClipboard(text, html)
    }
}

/**
 * Schneidet selektierten Text aus und kopiert in die Zwischenablage.
 */
export async function handleCut() {
    const target = _getActiveEditor()
    if (!target) return

    // rtData vor dem Cut lesen (handleCopy setzt es)
    const text = target.editor.handleCut()
    if (text) {
        const clip = target.editor.input._internalClipboard
        const rtData = clip?.rtData || null
        const outlineInfo = clip?.outlineInfo || null
        const paragraphTexts = clip?.paragraphTexts || null
        const html = _buildClipboardHtml(paragraphTexts, rtData, outlineInfo)
        await _writeClipboard(text, html)
        ctx.layout.isDirty = true
        // Save-Pfad: Memory hat eigenen Auto-Save im renderLoop
        if (!target.isMemory) saveContent()
    }
}

/**
 * Fügt Text aus der Zwischenablage ein.
 * Versucht HTML zu lesen (für Rich-Text aus Word/Notes etc.),
 * fällt auf Plain-Text zurück.
 */
export async function handlePaste() {
    const target = _getActiveEditor()
    if (!target) return

    try {
        let html = null
        let text = null

        // Clipboard API: HTML + Text lesen (wenn verfügbar)
        if (navigator.clipboard.read) {
            try {
                const items = await navigator.clipboard.read()
                for (const item of items) {
                    if (item.types.includes('text/html') && !html) {
                        const blob = await item.getType('text/html')
                        html = await blob.text()
                    }
                    if (item.types.includes('text/plain') && !text) {
                        const blob = await item.getType('text/plain')
                        text = await blob.text()
                    }
                }
            } catch {
                // Fallback: nur Plain-Text
                text = await navigator.clipboard.readText()
            }
        } else {
            text = await navigator.clipboard.readText()
        }

        if (html || text) {
            const result = target.editor.handlePaste(text || '', html)
            if (result === 'activateMain' && !target.isMemory) {
                ctx.layout.setActivePane(PaneId.MAIN)
                ctx.layout.exitNavigationMode()
            }
            ctx.layout.isDirty = true
            if (!target.isMemory) saveContent()
        }
    } catch (err) {
        console.warn('Paste failed:', err)
    }
}
